import React from 'react';
import { useDropzone } from 'react-dropzone';

import firebase from '../../utilities/firebase';
import RequestManager from '../../utilities/requestManager';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Popover from '@mui/material/Popover';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { alpha, useTheme } from '@mui/material/styles';
import { StackedDialog } from '../../utilities/ModalStackContext';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import BoltIcon from '@mui/icons-material/Bolt';
import DrawIcon from '@mui/icons-material/Draw';
import BlockIcon from '@mui/icons-material/Block';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PrintIcon from '@mui/icons-material/Print';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import InventoryIcon from '@mui/icons-material/Inventory2';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import TitleAppDialog from './TitleAppDialog';
import AffidavitDialog from './AffidavitDialog';
import PacketDates from './PacketDates';
import { useSelectMenuProps } from '../../utilities/ModalStackContext';
import { StateManager } from '../../utilities/stateManager';

const BUCKET = 'skyway-dev-373d5.appspot.com';
const BLUE = '#023e84';
const GOLD = '#ca9e66';

// How each requirement state reads, and which action the row offers.
const STATUS = {
  satisfied:         { label: 'In packet',           tone: 'success', icon: CheckCircleIcon },
  ready_to_cut:      { label: 'Ready to cut',        tone: 'info',    icon: ContentCutIcon, action: 'cut' },
  ready_to_make:     { label: 'Ready to make',       tone: 'info',    icon: NoteAddIcon,    action: 'make' },
  ready_to_attach:   { label: 'On file, not in packet', tone: 'info', icon: LinkIcon,       action: 'attach' },
  needs_signature:   { label: 'Not signed',          tone: 'warning', icon: DrawIcon },
  source_not_signed: { label: 'Awaiting signature',  tone: 'warning', icon: DrawIcon },
  missing:           { label: 'Missing',             tone: 'error',   icon: ErrorOutlineIcon },
  source_fault:      { label: 'Source problem',      tone: 'error',   icon: ErrorOutlineIcon },
  // The page cut by the page map is not the form it should be — almost always a
  // scan whose pages are in the wrong order. Refused rather than filed.
  wrong_page:        { label: 'Wrong page',          tone: 'error',   icon: FindInPageIcon },
  illegible:         { label: 'Cannot be read',      tone: 'error',   icon: FindInPageIcon },
};

const toneColor = (theme, tone) => ({
  success: theme.palette.success.dark,
  info: BLUE,
  warning: theme.palette.warning.dark,
  error: theme.palette.error.main,
}[tone] || theme.palette.text.secondary);

const openStoragePath = async (path) => {
  try {
    const url = await firebase.app().storage(BUCKET).ref(path).getDownloadURL();
    window.open(url, '_blank');
  } catch {
    StateManager.setAlertAndOpen('Could not open that file.', 'error');
  }
};

const printStoragePath = async (path, name) => {
  const ext = (path.split('.').pop() || '').toLowerCase();
  const url = await firebase.app().storage(BUCKET).ref(path).getDownloadURL();
  if (!['jpg', 'jpeg', 'png'].includes(ext)) return window.open(url, '_blank');
  const win = window.open('', '_blank', 'width=900,height=700');
  win.document.write(
    `<!DOCTYPE html><html><head><title>${name}</title>` +
    `<style>*{margin:0;padding:0;box-sizing:border-box}` +
    `body{display:flex;align-items:center;justify-content:center;min-height:100vh;background:#fff}` +
    `img{max-width:100%;max-height:100vh;object-fit:contain}` +
    `@media print{body{display:block}img{width:100%;max-height:none}}</style></head>` +
    `<body><img src="${url}" onload="setTimeout(()=>window.print(),400)"></body></html>`
  );
  win.document.close();
};

// ─── A required slot ─────────────────────────────────────────────────────────
// One row per document the deal needs, in printed order. The row knows how to
// fill itself: cut it, make it, attach it, or take an upload for this exact slot.
function RequirementRow({ index, req, isAdmin, isBusy, isUploading, onCut, onMake, onAttach, onUpload, onEditData, onWaive, onUnwaive, onAsk, onRemove, onAllowAgain }) {
  const theme = useTheme();
  const [dragOver, setDragOver] = React.useState(false);
  const meta = STATUS[req.status] || { label: req.status, tone: 'default', icon: RadioButtonUncheckedIcon };
  const Icon = req.overridden ? BlockIcon : meta.icon;
  const color = req.overridden ? theme.palette.text.secondary : toneColor(theme, meta.tone);
  const unmetBlocker = req.blocking && req.status !== 'satisfied' && !req.overridden;
  // A handful of rows are waivable by anyone. An admin can waive any of them,
  // because a real packet sometimes has a reason the rules table does not know —
  // and that waiver asks for the reason in writing.
  const openWaive = req.overridable && req.status !== 'satisfied' && !req.overridden;
  const adminWaive = !req.overridable && isAdmin && req.status !== 'satisfied' && !req.overridden;
  const canWaive = openWaive || adminWaive;
  // What the software worked out about whether this document is needed at all.
  const exemption = req.exemption || null;
  const autoWaived = !!(req.override && req.override.automatic);
  const inputId = `req-upload-${req.key}`;

  const busy = isBusy(req.key);
  // Held for reference, never printed — the title scans. It has no packet
  // position and offers no way to file it.
  const referenceOnly = !!req.reference_only;
  // Only a gap a person has to go and close is worth chasing a rep about. Anything
  // the build can finish is not their problem.
  const NEEDS_A_PERSON = ['missing', 'source_fault', 'needs_signature', 'source_not_signed',
                          'wrong_page', 'illegible'];
  const canAsk = !!req.owner && !req.overridden && NEEDS_A_PERSON.includes(req.status);
  const askWho = req.owner === 'consign_rep' ? 'consignment rep' : 'sales rep';
  const detail = [
    req.cut_from_page ? `page ${req.cut_from_page}` : null,
    req.would_cut_pages ? `page ${req.would_cut_pages.join(', ')}` : null,
    req.resolved_from || null,
  ].filter(Boolean).join(' · ');

  const uploading = isUploading(req.key);
  // A row that is still waiting takes a file dropped straight onto it. The upload
  // icon is a 16px target and the extras drop zone was the only thing on the card
  // that accepted a drag, so a file meant for one line landed loose in the packet
  // instead of filling the slot.
  const takesUpload = req.status !== 'satisfied' && !referenceOnly && !uploading && !busy;
  const onRowDragOver = (e) => {
    if (!takesUpload || !e.dataTransfer) return;
    if (!Array.from(e.dataTransfer.types || []).includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const onRowDrop = (e) => {
    if (!takesUpload) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) onUpload(req, file);
  };

  const act = {
    cut: { label: 'Cut', icon: <ContentCutIcon sx={{ fontSize: 14 }} />, run: () => onCut(req) },
    make: { label: 'Make', icon: <NoteAddIcon sx={{ fontSize: 14 }} />, run: () => onMake(req) },
    attach: { label: 'Add to packet', icon: <LinkIcon sx={{ fontSize: 14 }} />, run: () => onAttach(req) },
  }[meta.action];

  return (
    <Box
      onDragEnter={onRowDragOver}
      onDragOver={onRowDragOver}
      onDragLeave={(e) => {
        // dragleave also fires crossing between the row's own children, which
        // flickers the highlight off under the cursor.
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDragOver(false);
      }}
      onDrop={onRowDrop}
      data-sold-drop-ok
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.25, px: 1.5, py: 1, borderRadius: 1.5,
        bgcolor: dragOver ? alpha(BLUE, 0.06)
          : unmetBlocker ? alpha(theme.palette.error.main, 0.04) : 'transparent',
        border: dragOver ? '1px dashed' : '1px solid',
        borderColor: dragOver ? BLUE
          : unmetBlocker ? alpha(theme.palette.error.main, 0.18) : 'transparent',
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.disabled', width: 16, textAlign: 'right', mt: 0.4, flexShrink: 0 }}>
        {referenceOnly ? '—' : (req.status === 'satisfied' ? index : '·')}
      </Typography>
      <Icon sx={{ fontSize: 18, color, mt: 0.25, flexShrink: 0 }} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: req.status === 'satisfied' ? 500 : 600 }}>
            {req.label}
          </Typography>
          {req.blocking && (
            <Tooltip title="The state will not accept the packet without this">
              <Typography component="span" sx={{ color: 'error.main', fontWeight: 700, lineHeight: 1 }}>*</Typography>
            </Tooltip>
          )}
          <Typography variant="caption" sx={{ color, fontWeight: 600 }}>
            {autoWaived ? 'Not needed'
              : req.overridden ? 'Waived'
              : referenceOnly ? (req.status === 'satisfied' ? 'On file (not printed)' : meta.label)
              : meta.label}
          </Typography>
          {detail && <Typography variant="caption" sx={{ color: 'text.disabled' }}>{detail}</Typography>}
          {req.stale && (
            <Tooltip title={req.stale_note || 'The records changed after this page was made.'}>
              <Chip size="small" label="Out of date" color="warning" variant="outlined"
                sx={{ height: 18, fontSize: '0.68rem' }} />
            </Tooltip>
          )}
        </Box>

        {req.upload_hint && !req.note && !req.overridden && req.status !== 'satisfied' && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
            {req.upload_hint}
          </Typography>
        )}
        {req.note && !req.overridden && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
            {req.note}
          </Typography>
        )}
        {req.note_detail && !req.overridden && (
          <Tooltip title={req.note_detail}>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 0.25, cursor: 'help' }}>
              Technical detail
            </Typography>
          </Tooltip>
        )}
        {req.requested && !req.overridden && (
          <Typography variant="caption" sx={{ display: 'block', color: 'info.dark', mt: 0.25 }}>
            Asked {req.requested.owner === 'consign_rep' ? 'the consignment rep' : 'the sales rep'}
            {req.requested.requested_at ? ` on ${new Date(req.requested.requested_at).toLocaleDateString()}` : ''}
          </Typography>
        )}
        {req.overridden && autoWaived && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
            {req.override.reason}
            {exemption?.evidence ? ` ${exemption.evidence}` : ''}
          </Typography>
        )}
        {req.overridden && !autoWaived && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
            Waived{req.override?.by_name ? ` by ${req.override.by_name}` : ''}
            {req.override?.at ? ` on ${new Date(req.override.at).toLocaleDateString()}` : ''}
            {req.override?.reason ? ` — ${req.override.reason}` : ''}
            {' · still '}{(STATUS[req.status] || {}).label || req.status}
          </Typography>
        )}

        {/* Already in the packet, and a rule says the deal does not need it.
            There is nothing to ask: waiving a satisfied row does nothing, and the
            build takes the document out by itself. So say what is happening
            instead of offering a button that would change nothing. */}
        {exemption && req.status === 'satisfied' && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
            Not needed — {exemption.reason}
            {req.excused_in_packet ? ' It comes out of the packet on the next build.' : ''}
          </Typography>
        )}

        {/* Read off the paper title but not sure enough to stand on its own. The
            reason and the evidence are both here, so accepting it is an informed
            click rather than a leap.

            Only ever shown for a row that is NOT satisfied and NOT already
            waived. A rule that is sure enough waives the row on its own, so a
            "Waive it" button next to a document the software has already
            excused asks a person to confirm what it has just decided. */}
        {exemption && !req.overridden && req.status !== 'satisfied' && (
          <Box sx={{ mt: 0.5, p: 1, borderRadius: 1,
                     bgcolor: alpha(theme.palette.info.main, 0.06),
                     border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.25) }}>
            <Typography variant="caption" sx={{ display: 'block', color: 'info.dark', fontWeight: 700 }}>
              Probably not needed
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
              {exemption.reason}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled' }}>
              {exemption.evidence} · {exemption.confidence} confidence
            </Typography>
            <Button size="small" disabled={busy}
              onClick={() => onWaive(req, exemption.reason)}
              sx={{ fontSize: '0.72rem', py: 0.2, px: 1, mt: 0.25, color: BLUE, textTransform: 'none' }}>
              Waive it
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        {/* The line says what it is doing with its own file: several uploads run
            at once, and one spinner on the card cannot say which. */}
        {uploading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CircularProgress size={13} sx={{ color: BLUE }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Uploading…</Typography>
          </Box>
        )}

        {/* Any unmet slot takes a direct upload, so a missing insurance card is
            filed against the right requirement instead of landing loose. */}
        {!uploading && req.status !== 'satisfied' && !referenceOnly && (
          <>
            <input
              id={inputId}
              data-sold-ok
              type="file"
              accept="image/*,application/pdf,.heic,.heif"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(req, f); e.target.value = ''; }}
            />
            <Tooltip title="Upload this document">
              <label htmlFor={inputId} data-sold-ok>
                <IconButton size="small" component="span" disabled={busy}>
                  <UploadFileIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </label>
            </Tooltip>
          </>
        )}

        {canAsk && (
          <Tooltip title={`Notify the ${askWho} that this is needed`}>
            <Button
              size="small" disabled={busy} onClick={() => onAsk(req, !!req.requested)}
              startIcon={<NotificationsActiveIcon sx={{ fontSize: 14 }} />}
              sx={{ fontSize: '0.72rem', py: 0.2, px: 1, color: BLUE, textTransform: 'none' }}
            >
              {req.requested ? 'Ask again' : `Ask ${askWho}`}
            </Button>
          </Tooltip>
        )}
        {act && !referenceOnly && (
          <Button
            size="small" variant="outlined" disabled={busy}
            startIcon={busy ? <CircularProgress size={12} /> : act.icon}
            onClick={act.run}
            sx={{ fontSize: '0.72rem', py: 0.2, px: 1, borderColor: BLUE, color: BLUE, textTransform: 'none' }}
          >
            {busy ? 'Working…' : act.label}
          </Button>
        )}

        {onEditData && (
          <Tooltip title="Edit the title application data">
            <IconButton size="small" onClick={onEditData} sx={{ color: BLUE }}>
              <EditNoteIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )}

        {canWaive && (
          <Tooltip title={adminWaive
            ? 'The state normally requires this. Waiving it is an admin decision and needs a reason.'
            : 'Waive this requirement'}>
            <Button size="small" disabled={busy} onClick={() => onWaive(req)}
              sx={{ fontSize: '0.72rem', py: 0.2, px: 1, color: 'text.secondary', textTransform: 'none' }}>
              {adminWaive ? 'Waive (admin)' : 'Waive'}
            </Button>
          </Tooltip>
        )}
        {req.overridden && (
          <Button size="small" disabled={busy} onClick={() => onUnwaive(req)}
            sx={{ fontSize: '0.72rem', py: 0.2, px: 1, color: BLUE, textTransform: 'none' }}>
            Undo
          </Button>
        )}

        {/* The page we refused is kept out of the packet but not thrown away —
            seeing it is how a person confirms the scan is out of order. */}
        {req.rejected_path && (
          <Tooltip title="View the page that was refused">
            <IconButton size="small" data-sold-ok onClick={() => openStoragePath(req.rejected_path)}>
              <FindInPageIcon sx={{ fontSize: 15, color: 'error.main' }} />
            </IconButton>
          </Tooltip>
        )}

        {req.path && (
          <Tooltip title="View">
            <IconButton size="small" data-sold-ok onClick={() => openStoragePath(req.path)}>
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* A filled slot can be emptied — someone uploads the wrong file. */}
        {req.in_packet && (
          <Tooltip title="Take this document out of the packet">
            <IconButton size="small" disabled={busy} onClick={() => onRemove(req)} sx={{ color: 'error.light' }}>
              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Removed on purpose, so the build leaves it alone until told otherwise. */}
        {req.suppressed && (
          <Tooltip title="Let the packet use the file on the record again">
            <Button size="small" disabled={busy} onClick={() => onAllowAgain(req)}
              sx={{ fontSize: '0.72rem', py: 0.2, px: 1, color: BLUE, textTransform: 'none' }}>
              Use it again
            </Button>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

// ─── A file nobody asked for ─────────────────────────────────────────────────
// The special cases: an out-of-state assignment, a one-off letter, a scan the
// clerk wants in the stack. These are not required, so they sit apart.
// A held file stays on the card for reference but is kept out of the packet.
function ExtraFileRow({ file, held, onView, onPrint, onDelete, onRename, onToggleHeld }) {
  const theme = useTheme();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(file.name);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== file.name) onRename(trimmed);
    setEditing(false);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.9, borderRadius: 1.5,
               bgcolor: alpha(theme.palette.grey[100], 0.7), border: '1px solid', borderColor: 'divider',
               borderStyle: held ? 'dashed' : 'solid' }}>
      <InventoryIcon sx={{ fontSize: 16, color: held ? 'text.disabled' : GOLD, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <TextField
            value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') { setDraft(file.name); setEditing(false); } }}
            size="small" autoFocus fullWidth variant="standard"
            sx={{ '& .MuiInput-input': { fontSize: '0.875rem' } }}
          />
        ) : (
          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            color: held ? 'text.secondary' : 'text.primary' }}>
            {file.name}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {file.date}{held ? ' · Not in packet' : ''}
        </Typography>
      </Box>
      <Tooltip title={held ? 'Put back in the packet' : 'Keep on file, leave out of the packet'}>
        <IconButton size="small" onClick={onToggleHeld}>
          {held ? <AddCircleOutlineIcon sx={{ fontSize: 14 }} /> : <RemoveCircleOutlineIcon sx={{ fontSize: 14 }} />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Rename"><IconButton size="small" onClick={() => { setDraft(file.name); setEditing(true); }}><EditIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
      <Tooltip title="View"><IconButton size="small" onClick={onView} data-sold-ok><OpenInNewIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
      <Tooltip title="Print"><IconButton size="small" onClick={onPrint} data-sold-ok><PrintIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
      <Tooltip title="Remove"><IconButton size="small" onClick={onDelete} sx={{ color: 'error.light' }}><DeleteOutlineIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
    </Box>
  );
}

// How each tag choice reads inside the summary sentence. The sheet's own
// columns are New / Transfer Only / Transfer & Renew, and `none` is a row with
// no tag at all — a fast title and nothing else.
const TAG_LABELS = {
  none: 'no tag',
  new: 'a new tag',
  transfer_only: 'a transferred tag',
  transfer_renew: 'a transferred tag + renewal',
};

// A value in the summary sentence that a person can change.
//
// The facts — where the title goes, whether the consignor signed it over, and
// which tag the agency issues — are decisions about the DEAL, not actions on a
// document. As open dropdowns they took eight lines at the top of the card and
// pushed the document list out of sight. Here each one reads as part of a
// sentence, and the software's reasoning appears when you question the value
// rather than always.
function FactValue({ label, value, options, reason, setByName, disagrees, busy, onPick, onAutomatic }) {
  const [anchor, setAnchor] = React.useState(null);
  const current = options.find(o => o.value === value) || options[0];

  return (
    <>
      <Box
        component="span"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          cursor: 'pointer',
          fontWeight: 600,
          color: disagrees ? 'warning.dark' : 'text.primary',
          borderBottom: '1px dashed',
          borderColor: 'text.disabled',
          '&:hover': { borderColor: 'text.primary' },
        }}
      >
        {current ? current.short : '—'}
      </Box>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, maxWidth: 320 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
          </Typography>
          <RadioGroup
            value={String(value)}
            onChange={(e) => { onPick(e.target.value); setAnchor(null); }}
          >
            {options.map(o => (
              <FormControlLabel
                key={String(o.value)}
                value={String(o.value)}
                control={<Radio size="small" disabled={busy} />}
                label={<Typography variant="body2">{o.label}</Typography>}
              />
            ))}
          </RadioGroup>

          {reason && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
              {setByName
                ? `Set by ${setByName}. The software would have said: ${reason}`
                : reason}
            </Typography>
          )}

          {setByName && (
            <Button size="small" disabled={busy} sx={{ mt: 0.5, textTransform: 'none' }}
              onClick={() => { onAutomatic(); setAnchor(null); }}>
              Use the automatic answer
            </Button>
          )}
        </Box>
      </Popover>
    </>
  );
}

// One line for one signed source, with everything else behind the button.
function SourceLine({ label, source, era, uploading, onOpen }) {
  const usable = source.path && !source.fault;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
      <Typography variant="caption" sx={{ flex: 1, color: source.fault ? 'error.main' : 'text.secondary' }}>
        {label}:{' '}
        {source.fault
          ? source.fault
          : source.path
            ? `${source.template || '—'} · ${source.pageCount} pages · page map ${source.mapOk ? 'OK' : 'MISMATCH'}`
            : 'nothing signed on this car yet'}
        {era === 'legacy' && ' · pre-e-signature deal'}
        {source.chosen_by_person ? ' · you chose this' : ''}
      </Typography>
      {uploading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <CircularProgress size={13} sx={{ color: BLUE }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Uploading…</Typography>
        </Box>
      ) : (
        <Button size="small" onClick={onOpen} sx={{ textTransform: 'none', flexShrink: 0 }}>
          {usable ? 'Change' : 'Add'}
        </Button>
      )}
    </Box>
  );
}

// Which signed PDF the packet cuts its pages out of.
//
// Choosing between signed documents, seeing why one cannot be read, and
// uploading a replacement are one question asked occasionally, so they belong
// behind a button rather than on the card. Drag a file in or click to browse —
// `useDropzone` gives both from one component.
function SourceDialog({ open, onClose, title, source, sourceField, onChoose, onReplace, busy }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    // react-dropzone v11 wants a comma-separated string, not the v12 object
    // map. An object reaches attr-accept, which calls .split(',') on it and
    // throws, taking the whole modal down on drop and breaking the picker.
    accept: '.pdf,application/pdf',
    multiple: false,
    onDrop: (files) => { if (files && files[0]) onReplace(sourceField, files[0]); },
  });
  const candidates = source.candidates || [];

  return (
    <StackedDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent>
        {source.fault ? (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />
            <Box>
              <Typography variant="body2" sx={{ color: 'error.main' }}>{source.fault}</Typography>
              {source.fault_detail && (
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>{source.fault_detail}</Typography>
              )}
            </Box>
          </Box>
        ) : source.path ? (
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {source.template || 'Signed document'} · {source.pageCount} pages · page map {source.mapOk ? 'OK' : 'MISMATCH'}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Nothing signed is on this car yet.
          </Typography>
        )}

        {candidates.length > 1 && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Which one to cut from
            </Typography>
            <RadioGroup
              value={source.path || ''}
              onChange={(e) => onChoose(sourceField, e.target.value)}
            >
              {candidates.map(c => (
                <FormControlLabel
                  key={c.path}
                  value={c.path}
                  control={<Radio size="small" disabled={busy} />}
                  label={
                    <Typography variant="body2">
                      {c.label}{c.pageCount ? ` · ${c.pageCount} pages` : ''}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
            <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.disabled' }}>
              Pages cut from the previous document are dropped, so a packet never mixes two signings.
            </Typography>
          </>
        )}

        <Box
          {...getRootProps()}
          sx={{
            mt: 1, p: 2.5, textAlign: 'center', cursor: 'pointer',
            border: '1px dashed', borderRadius: 1,
            borderColor: isDragActive ? BLUE : 'divider',
            bgcolor: isDragActive ? alpha(BLUE, 0.05) : 'transparent',
          }}
        >
          <input {...getInputProps()} />
          <UploadFileIcon sx={{ fontSize: 22, color: BLUE }} />
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {isDragActive ? 'Drop it here' : 'Drag the signed PDF here, or click to choose a file'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            The whole signed packet as one PDF. Every page inside it is then cut automatically.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </StackedDialog>
  );
}

function ExtraDropzone({ onDrop, uploading }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true, accept: 'image/*,.pdf,.heic,.heif', disabled: uploading,
    onDrop: (files) => { if (files.length) onDrop(files); },
  });
  return (
    <Box {...getRootProps()} sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, p: 1.75,
      border: '2px dashed', borderColor: isDragActive ? BLUE : 'divider', borderRadius: 2,
      bgcolor: isDragActive ? alpha(BLUE, 0.04) : alpha(GOLD, 0.02),
      cursor: uploading ? 'default' : 'pointer',
      '&:hover': uploading ? {} : { borderColor: BLUE, bgcolor: alpha(BLUE, 0.03) },
    }}>
      <input {...getInputProps()} />
      {uploading ? <CircularProgress size={18} sx={{ color: BLUE }} /> : <CloudUploadIcon sx={{ fontSize: 20, color: BLUE }} />}
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        {uploading ? 'Uploading…' : isDragActive ? 'Drop to add' : 'Drag in an extra document for a special case'}
      </Typography>
    </Box>
  );
}

// ─── The packet ──────────────────────────────────────────────────────────────
export default function DmvPacket({ car = {}, onPacketChanged }) {
  const theme = useTheme();
  const menuProps = useSelectMenuProps();
  const stockNumber = car.stock;

  const [loading, setLoading] = React.useState(true);
  // A set, not one key: several actions can be in flight at once, and each row
  // has to keep its own spinner until its own request finishes.
  const [busyKeys, setBusyKeys] = React.useState(() => new Set());
  const isBusy = React.useCallback((key) => busyKeys.has(key), [busyKeys]);
  const startBusy = React.useCallback((key) => {
    setBusyKeys(prev => { const next = new Set(prev); next.add(key); return next; });
  }, []);
  const endBusy = React.useCallback((key) => {
    setBusyKeys(prev => { const next = new Set(prev); next.delete(key); return next; });
  }, []);
  // Which requirement rows have a file going up. Separate from `busyKeys` so the
  // row can say "Uploading…" rather than only greying its buttons out.
  const [uploadingKeys, setUploadingKeys] = React.useState(() => new Set());
  const isUploading = React.useCallback((key) => uploadingKeys.has(key), [uploadingKeys]);
  const [uploadingExtra, setUploadingExtra] = React.useState(false);
  const [compiling, setCompiling] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [packet, setPacket] = React.useState({ files: [] });
  const [error, setError] = React.useState(null);
  const [titleDataOpen, setTitleDataOpen] = React.useState(false);
  // Which signed source the dialog is open for: 'sales_doc', 'consignment_doc',
  // or null for closed.
  const [sourceDialog, setSourceDialog] = React.useState(null);
  const [affidavitOpen, setAffidavitOpen] = React.useState(false);

  const readPacketDoc = React.useCallback(async () => {
    if (!stockNumber) return;
    const snap = await firebase.firestore().doc('dmv_packet/' + stockNumber).get();
    setPacket(snap.exists ? { files: [], ...snap.data() } : { files: [] });
  }, [stockNumber]);

  // Opening the tab only reads. `dryRun` keeps it from writing an evaluation.
  const load = React.useCallback(async (persist = false) => {
    if (!stockNumber) return;
    setLoading(true);
    setError(null);
    const [res] = await Promise.all([
      RequestManager.post({ function: 'evaluateDmvPacket', variables: { stockNumber, dryRun: !persist } }),
      readPacketDoc(),
    ]);
    if (res && res.success) setData(res);
    else setError((res && (res.message || res.error)) || 'Could not work out what this packet needs.');
    setLoading(false);
  }, [stockNumber, readPacketDoc]);

  React.useEffect(() => { load(false); }, [load]);

  const after = async (msg, tone = 'success') => {
    if (msg) StateManager.setAlertAndOpen(msg, tone);
    onPacketChanged?.();
    await load(false);
  };

  const callFor = async (req, fn, variables, verb) => {
    startBusy(req.key);
    const res = await RequestManager.post({ function: fn, variables });
    endBusy(req.key);
    if (!res || res.success === false) {
      StateManager.setAlertAndOpen((res && (res.message || res.error)) || `Could not ${verb}.`, 'error');
      return;
    }
    if (res.verification && res.verification.signed_by_owner === false) {
      return after(`${res.label} was added but is NOT signed by the owner.`, 'warning');
    }
    return after(`${res.label || req.label} added to the packet.`);
  };

  const onCut = (req) => callFor(req, 'cutDmvPacketPage',
    { stockNumber, pageRole: req.page_role, sourceField: req.from, requirementKey: req.key }, 'cut that page');

  const onMake = (req) => callFor(req, 'generateDmvDocument',
    { stockNumber, doctype: req.doctype, requirementKey: req.key }, 'make that document');

  const onAttach = (req) => callFor(req, 'attachDmvDocument',
    { stockNumber, requirementKey: req.key }, 'add that to the packet');

  // Upload straight into a required slot. The file lands with the requirement key
  // on it, so the slot fills instead of the file sitting loose in the packet.
  const onUpload = async (req, file) => {
    startBusy(req.key);
    setUploadingKeys(prev => { const next = new Set(prev); next.add(req.key); return next; });
    const done = () => {
      endBusy(req.key);
      setUploadingKeys(prev => { const next = new Set(prev); next.delete(req.key); return next; });
    };
    try {
      const raw = (file.name.split('.').pop() || '').toLowerCase();
      let ext = raw || (file.type === 'application/pdf' ? 'pdf' : 'jpg');
      let path = `dmv_packets/${stockNumber}/uploads/${req.key}.${ext}`;
      await firebase.app().storage(BUCKET).ref(path).put(file);

      if (['heic', 'heif'].includes(ext)) {
        const conv = await RequestManager.post({ function: 'convertHeicToJpeg', variables: { storagePath: path } });
        if (conv && conv.success && conv.jpegPath) { path = conv.jpegPath; ext = 'jpg'; }
      }

      const entry = {
        name: `${stockNumber} — ${req.label}.${ext}`,
        path, ext,
        date: new Date().toISOString().slice(0, 10).replace(/-/g, '/'),
        requirement_key: req.key,
        source: 'upload',
      };
      // Filed server-side in a transaction, so two uploads at once cannot drop
      // each other's entry.
      const res = await RequestManager.post({
        function: 'fileDmvPacketEntries',
        variables: { stockNumber, entries: [entry] },
      });
      done();
      // The file reaching Storage is not the same as the slot being filled. This
      // used to report success either way, so a refusal read as a document that
      // was in the packet and was not.
      if (!res || res.success === false) {
        return StateManager.setAlertAndOpen(
          (res && (res.message || res.error)) || `${req.label} could not be filed into the packet.`, 'error');
      }
      await after(`${req.label} uploaded into the packet.`);
    } catch (e) {
      done();
      StateManager.setAlertAndOpen(`Upload failed: ${e.message}`, 'error');
    }
  };

  // Waiving a row the rules table calls mandatory is an admin decision, and the
  // server insists on a written reason for it. Ask here so the refusal never
  // reaches the user. `suggestedReason` is what the exemption check worked out —
  // accepting its suggestion should not make anybody retype it.
  const setWaiver = async (req, suggestedReason, remove) => {
    let reason = suggestedReason || '';
    if (!remove && !req.overridable) {
      reason = window.prompt(
        `"${req.label}" is a document the state normally requires.\n\nWhy is it not needed on this deal?`,
        reason,
      );
      if (reason === null) return;
      if (!String(reason).trim()) {
        return StateManager.setAlertAndOpen('A waiver needs a reason.', 'warning');
      }
    }

    startBusy(req.key);
    const res = await RequestManager.post({
      function: 'overrideDmvRequirement',
      variables: {
        stockNumber, requirementKey: req.key,
        userId: StateManager.userID || null, userName: StateManager.userName || null,
        reason: reason || '',
        remove: !!remove,
      },
    });
    endBusy(req.key);
    if (!res || res.success === false) {
      return StateManager.setAlertAndOpen((res && (res.message || res.error)) || 'Could not change the waiver.', 'error');
    }
    await after(remove ? `${req.label} is required again.` : `${req.label} waived.`);
  };

  // Ask the software which documents this deal does not need. It reads the paper
  // title when it has to, so it is worth a button rather than a page load.
  const checkExemptions = async (force = false) => {
    startBusy('exemptions');
    const res = await RequestManager.post({
      function: 'checkDmvExemptions',
      variables: { stockNumber, force: !!force },
    });
    endBusy('exemptions');
    if (!res || res.success === false) {
      return StateManager.setAlertAndOpen(
        (res && (res.message || res.error)) || 'Could not check what is needed.', 'error');
    }

    if (res.evaluation) setData({ success: true, ...res.evaluation });
    await readPacketDoc();
    onPacketChanged?.();

    const applied = (res.applied || []).length;
    const suggested = (res.suggested || []).length;
    if (applied || suggested) {
      return StateManager.setAlertAndOpen(
        [applied ? `${applied} document${applied === 1 ? '' : 's'} not needed` : null,
         suggested ? `${suggested} to look at` : null].filter(Boolean).join(' · '), 'success');
    }
    if (res.reason === 'no_title_image') {
      return StateManager.setAlertAndOpen(
        'There is no photograph of the title to read, so nothing could be ruled out.', 'warning');
    }
    StateManager.setAlertAndOpen('Everything on the list is still needed.', 'info');
  };

  // Correct a fact the software read wrong — which way the title goes, or whether
  // the consignor signed it over. The rules table still decides what the deal
  // needs; this only changes what it is told. `remove` hands the question back.
  //
  // Changing the track changes the document list, so the server also clears
  // anything the change orphaned. Say so, because those pages would otherwise
  // still be in the printed stack.
  const setFact = async (factKey, value, { remove = false, reason = '' } = {}) => {
    startBusy(`fact-${factKey}`);
    const res = await RequestManager.post({
      function: 'setDmvPacketFact',
      variables: { stockNumber, factKey, value, reason, remove: !!remove },
    });
    endBusy(`fact-${factKey}`);
    if (!res || res.success === false) {
      return StateManager.setAlertAndOpen(
        (res && (res.message || res.error)) || 'Could not change that.', 'error');
    }

    const removedCount = (res.files_removed || []).length;
    // Switching to or from the duplicate packet parks documents instead of
    // removing them, so the other packet gets them back.
    const parkedCount = (res.files_parked || []).length;
    const restoredCount = (res.files_restored || []).length;
    const note = (removedCount
      ? ` ${removedCount} document${removedCount === 1 ? '' : 's'} this deal no longer needs ${removedCount === 1 ? 'was' : 'were'} taken out of the packet.`
      : '')
      + (parkedCount ? ` ${parkedCount} set aside until the packet switches back.` : '')
      + (restoredCount ? ` ${restoredCount} put back from before.` : '');
    StateManager.setAlertAndOpen(
      (remove ? 'Back to the automatic answer.' : 'Updated.') + note, 'success');

    if (res.evaluation) setData({ success: true, ...res.evaluation });
    await readPacketDoc();
    onPacketChanged?.();
  };

  // Generate a form the rules do not ask for — a duplicate title application, an
  // affidavit. It goes in as an extra with no requirement key, so the packet
  // never waits on it, and prints in the right place rather than after the loose
  // uploads.
  //
  // The affidavit is the exception: it swears to something a person has to put
  // in words, so it asks first. See AffidavitDialog.
  const makeExtraForm = async (doctype) => {
    if (doctype === 'affidavit') return setAffidavitOpen(true);

    startBusy('extra-form');
    const res = await RequestManager.post({
      function: 'generateDmvDocument',
      variables: { stockNumber, doctype, asExtra: true },
    });
    endBusy('extra-form');

    // Some state forms are published locked against changes, so they cannot be
    // filled or merged. The form is still the right one, so open the blank for
    // the clerk to print and complete by hand — the finished scan goes back in
    // through the ordinary upload.
    if (res && res.reason === 'template_encrypted') {
      if (res.blankUrl) window.open(res.blankUrl, '_blank');
      return StateManager.setAlertAndOpen(
        `${res.label} cannot be filled in automatically, so the blank has been opened for printing.`,
        'warning');
    }

    if (!res || res.success === false || res.ok === false) {
      return StateManager.setAlertAndOpen(
        (res && (res.message || res.error)) || 'Could not make that form.', 'error');
    }
    await after(`${res.label} added to the packet.`);
  };

  // Pin which signed document the packet cuts from. Pages cut from the previous one
  // are dropped server-side, so a packet cannot mix two signings.
  const chooseSource = async (sourceField, storagePath) => {
    startBusy('choose-source');
    const res = await RequestManager.post({
      function: 'chooseDmvSourceDocument',
      variables: { stockNumber, sourceField, storagePath, chosenBy: StateManager.userID || null },
    });
    endBusy('choose-source');
    if (!res || res.success === false) {
      return StateManager.setAlertAndOpen((res && (res.message || res.error)) || 'Could not switch document.', 'error');
    }
    const dropped = res.droppedStaleCuts ? ` ${res.droppedStaleCuts} page(s) cut from the previous document were removed.` : '';
    StateManager.setAlertAndOpen(`Now using ${res.chosen ? res.chosen.label : 'the selected document'}.${dropped}`, 'success');
    if (res.evaluation) setData({ success: true, ...res.evaluation });
    await readPacketDoc();
    onPacketChanged?.();
  };

  // The signed paperwork a car points at is sometimes not in Storage — older deals
  // whose PDF never reached our bucket. Uploading it again repoints the car, and
  // every page inside it becomes cuttable, so one upload clears several rows.
  const replaceSource = async (sourceField, file) => {
    const busyKey = `replace-source-${sourceField}`;
    startBusy(busyKey);
    try {
      const path = `dmv_packets/${stockNumber}/source/${sourceField}.pdf`;
      await firebase.app().storage(BUCKET).ref(path).put(file);

      const res = await RequestManager.post({
        function: 'replaceDmvSourceDocument',
        variables: { stockNumber, sourceField, storagePath: path, replacedBy: StateManager.userID || null },
      });
      endBusy(busyKey);

      if (!res || res.success === false) {
        // A refused upload is a real answer with a reason worth reading.
        return StateManager.setAlertAndOpen((res && (res.message || res.error)) || 'That file could not be used.', 'warning');
      }
      StateManager.setAlertAndOpen(
        `Signed paperwork replaced — ${res.pageCount} pages. The documents inside it can now be cut.`, 'success');
      await after(null);
    } catch (e) {
      endBusy(busyKey);
      StateManager.setAlertAndOpen(`Upload failed: ${e.message}`, 'error');
    }
  };

  // Take a document out of a required slot. What the packet made or cut is deleted;
  // an attached customer document is only unlinked, because the file belongs to the
  // customer record and other parts of the app read it.
  const removeOne = async (req) => {
    const attached = req.verification === 'attached_from_record';
    const question = attached
      ? `Take "${req.label}" out of this packet?\n\nThe customer's own file stays where it is — only the packet stops using it. ` +
        `The packet will not pick it up again until you upload the right file or choose "Use it again".`
      : `Delete "${req.label}" from this packet?\n\nThe file itself is removed. It can be cut or made again afterwards.`;
    if (!window.confirm(question)) return;

    // Show it gone straight away and confirm behind it. A failure puts it back.
    const snapshot = data;
    setData(prev => prev && ({
      ...prev,
      requirements: prev.requirements.map(r => r.key === req.key
        ? { ...r, status: 'missing', in_packet: false, path: null, verification: null }
        : r),
    }));
    setPacket(prev => ({ ...prev, files: (prev.files || []).filter(f => f.requirement_key !== req.key) }));

    const res = await RequestManager.post({
      function: 'removeDmvPacketEntry',
      variables: { stockNumber, requirementKey: req.key, removedBy: StateManager.userID || null },
    });

    if (!res || res.success === false) {
      setData(snapshot);
      await readPacketDoc();
      return StateManager.setAlertAndOpen((res && (res.message || res.error)) || 'Could not remove that document.', 'error');
    }
    if (res.evaluation) setData({ success: true, ...res.evaluation });
    StateManager.setAlertAndOpen(res.leftInPlace
      ? `${req.label} removed from the packet. The customer's file was left alone.`
      : `${req.label} removed.`, 'success');
    await readPacketDoc();
    onPacketChanged?.();
  };

  const allowAgain = async (req) => {
    startBusy(req.key);
    const res = await RequestManager.post({
      function: 'allowDmvAttachmentAgain',
      variables: { stockNumber, requirementKey: req.key },
    });
    endBusy(req.key);
    if (!res || res.success === false) {
      return StateManager.setAlertAndOpen((res && (res.message || res.error)) || 'Could not change that.', 'error');
    }
    await after(`${req.label} can be taken from the record again.`);
  };

  // Tell the responsible rep what is missing. With no requirement, asks every rep
  // who owes something.
  // `force` is what "Ask again" means: send a fresh nudge even though the same ask
  // was already made. Without it the dedupe guard silently swallows the repeat.
  const askFor = async (req, force = false) => {
    const key = req ? req.key : 'ask-all';
    startBusy(key);
    const res = await RequestManager.post({
      function: 'requestDmvDocuments',
      variables: {
        stockNumber,
        requirementKeys: req ? [req.key] : null,
        requestedBy: StateManager.userID || null,
        force,
      },
    });
    endBusy(key);

    if (!res) return StateManager.setAlertAndOpen('Could not send the request.', 'error');

    const sent = res.sent || [];
    const already = res.already_asked || [];
    const blocked = res.blocked || [];

    if (sent.length) {
      const who = sent.map(g => g.who || (g.owner === 'consign_rep' ? 'the consignment rep' : 'the sales rep'));
      StateManager.setAlertAndOpen(`Asked ${who.join(' and ')}.`, 'success');
    } else if (already.length) {
      // Being told again is not a failure — say who already knows, and that
      // "Ask again" will nudge them.
      const a = already[0];
      const when = a.asked_at ? ` on ${new Date(a.asked_at).toLocaleDateString()}` : '';
      StateManager.setAlertAndOpen(
        `${a.who || 'They'} were already asked${when}. Use "Ask again" to nudge.`, 'info');
    } else if (blocked.length) {
      StateManager.setAlertAndOpen(blocked[0].reason, 'warning');
    } else {
      StateManager.setAlertAndOpen(res.message || 'There is nothing outstanding to ask for.', 'info');
    }
    // A blockage alongside a send is still worth saying.
    if (sent.length && blocked.length) StateManager.setAlertAndOpen(blocked[0].reason, 'warning');

    await after(null);
  };

  const buildPacket = async () => {
    startBusy('build');
    const res = await RequestManager.post({ function: 'buildDmvPacket', variables: { stockNumber } });
    endBusy('build');
    if (!res || res.success === false) {
      return StateManager.setAlertAndOpen((res && (res.message || res.error)) || 'Could not build the packet.', 'error');
    }
    const added = [...(res.cuts || []), ...(res.made || []), ...(res.attached || [])].filter(x => x.ok).length;
    const unsigned = (res.cuts || []).filter(c => c.ok && c.verification && c.verification.signed_by_owner === false).length;
    const outcome = res.outcome || {};

    if (unsigned) StateManager.setAlertAndOpen(`${added} document(s) added, but ${unsigned} is not signed by the owner.`, 'warning');
    else if (outcome.clean) StateManager.setAlertAndOpen(`${added} document(s) added. Packet complete — moved to ready for processing.`, 'success');
    else if (added) StateManager.setAlertAndOpen(`${added} document(s) added. ${(outcome.reasons || []).length} still need a person.`, 'warning');
    else StateManager.setAlertAndOpen('Nothing left that can be built automatically.', 'info');

    await after(null);
  };

  // ── Extra files, for the special cases ────────────────────────────────────
  const updateFiles = async (files) => {
    setPacket(prev => ({ ...prev, files }));
    await firebase.firestore().doc('dmv_packet/' + stockNumber).set({ files }, { merge: true });
    onPacketChanged?.();
  };

  // A held file lives in held_files, which no compile step reads. Moving it
  // between the two lists is the whole of "leave it out of the packet".
  const setHeld = async (file, hold) => {
    const same = f => f.path === file.path;
    const move = (doc) => {
      const files = (doc.files || []).filter(f => !same(f));
      const held_files = (doc.held_files || []).filter(f => !same(f));
      return hold ? { files, held_files: [...held_files, file] } : { files: [...files, file], held_files };
    };
    setPacket(prev => ({ ...prev, ...move(prev) }));
    try {
      // Move against the stored lists, not the screen's, so a build that ran
      // in the meantime is not overwritten.
      const ref = firebase.firestore().doc('dmv_packet/' + stockNumber);
      await firebase.firestore().runTransaction(async (tx) => {
        const snap = await tx.get(ref);
        tx.set(ref, move(snap.exists ? snap.data() : {}), { merge: true });
      });
      onPacketChanged?.();
      return true;
    } catch {
      await readPacketDoc();
      StateManager.setAlertAndOpen(`Could not ${hold ? 'take' : 'put'} "${file.name}" ${hold ? 'out of' : 'back in'} the packet.`, 'error');
      return false;
    }
  };

  const onDropExtras = async (incoming) => {
    setUploadingExtra(true);
    const entries = [];
    for (const file of incoming) {
      try {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        let path = `dmv_packets/${stockNumber}/${Date.now()}-${file.name}`;
        await firebase.app().storage(BUCKET).ref(path).put(file);
        if (['heic', 'heif'].includes(ext)) {
          const conv = await RequestManager.post({ function: 'convertHeicToJpeg', variables: { storagePath: path } });
          if (conv && conv.success && conv.jpegPath) path = conv.jpegPath;
        }
        entries.push({
          name: file.name.replace(/\.(heic|heif)$/i, '.jpg'), path,
          date: new Date().toISOString().slice(0, 10).replace(/-/g, '/'),
          source: 'manual',
        });
      } catch {
        StateManager.setAlertAndOpen(`Failed to upload ${file.name}`, 'error');
      }
    }
    if (entries.length) {
      const res = await RequestManager.post({
        function: 'fileDmvPacketEntries',
        variables: { stockNumber, entries },
      });
      if (!res || res.success === false) {
        StateManager.setAlertAndOpen(
          (res && (res.message || res.error)) || 'Those files could not be filed into the packet.', 'error');
      }
      await readPacketDoc();
      onPacketChanged?.();
    }
    setUploadingExtra(false);
  };

  const compile = async () => {
    setCompiling(true);
    StateManager.setAlertAndOpen('Compiling the packet…', 'info');
    const res = await RequestManager.post({ function: 'compileDmvPacket', variables: { stockNumber } });
    setCompiling(false);
    if (!res || res.success === false) {
      return StateManager.setAlertAndOpen((res && (res.message || res.error)) || 'Could not compile the packet.', 'error');
    }
    setPacket(prev => ({ ...prev, compiled_at: res.compiled_at, compiled_path: res.path }));
    window.open(res.url, '_blank');
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  // The same test the server applies. It decides what the screen offers; the
  // server decides what is allowed.
  const isAdmin = StateManager.isManager();
  const requirements = (data && data.requirements) || [];
  const readiness = (data && data.readiness) || null;
  const source = (data && data.source_packet) || {};
  const sourceConsign = (data && data.source_consignment) || {};
  const summary = (data && data.summary) || {};
  const blockers = readiness ? readiness.blocking_missing : 0;
  const pct = readiness ? readiness.percent : 0;

  // The clerk-correctable facts. An older backend does not send them, so each
  // one falls back to an empty shape and the screen still renders.
  const facts = (data && data.facts) || {};
  const trackFact = facts.track || {};
  const trackAuto = trackFact.auto || {};
  const consignorFact = facts.consignor_signed_title || {};
  const consignorAuto = consignorFact.auto || {};
  const tagFact = facts.tag_type || {};
  const tagAuto = tagFact.auto || {};
  const duplicateFact = facts.needs_duplicate || {};
  const duplicateAuto = duplicateFact.auto || {};

  // Dates that do not hold up. Never blocking — the office is the one that gets
  // to argue about a date, so these inform the clerk and nothing else.
  const dateFindingList = (data && data.date_findings) || [];
  const lastTransfer = (data && data.last_transfer) || {};

  // Files in the packet that no requirement claims any more. They still PRINT —
  // the compile step merges the file list without consulting the rules — so this
  // is not cosmetic.
  const orphanedFiles = (data && data.orphaned_files) || [];

  // What the last build settled on. "Waiting on a rep who has been told" and
  // "nobody can move this" are different situations and are shown differently.
  //
  // The live evaluation can be fresher than the stored state — somebody may have
  // just uploaded the last document — so nothing outstanding always reads ready.
  const state = blockers === 0 ? 'ready'
    : (packet.packet_state && packet.packet_state !== 'ready') ? packet.packet_state
    : 'needs_review';
  const waitingOn = (packet.waiting_on || []).map(o =>
    o === 'consign_rep' ? 'the consignment rep' : 'the sales rep');
  // A page made from older records counts as something to build: the build
  // remakes it.
  const staleCount = requirements.filter(r => r.stale).length;
  const canBuild = staleCount > 0 || requirements.some(r => String(r.status).startsWith('ready_to'));
  // Only worth offering when something an exemption could reach is holding the
  // packet up, or when it already has and a person may want it re-read.
  // Which rows a rule could excuse comes from the server, so this cannot drift
  // out of step with the table. An older backend does not send it.
  const exemptableKeys = (data && data.exemptable_keys) || ['consignor_poa', 'consignor_dl'];
  // There is nothing to check on a row that is already satisfied — the document
  // is in the packet, and if a rule excuses it the build takes it out without
  // being asked.
  const exemptable = requirements.filter(r =>
    exemptableKeys.includes(r.key) && r.status !== 'satisfied');
  const askable = requirements.filter(r =>
    r.owner && !r.overridden &&
    ['missing', 'source_fault', 'needs_signature', 'source_not_signed',
     'wrong_page', 'illegible'].includes(r.status));
  const requiredKeys = new Set(requirements.map(r => r.key));
  const extras = (packet.files || []).filter(f => !f.requirement_key || !requiredKeys.has(f.requirement_key));
  const heldFiles = packet.held_files || [];
  const inPacketCount = requirements.filter(r => r.in_packet === true).length + extras.length;

  let satisfiedSeen = 0;

  return (
    <Paper elevation={0} sx={{
      border: '1px solid',
      borderColor: readiness && blockers === 0 ? alpha(theme.palette.success.main, 0.35) : theme.palette.divider,
      borderRadius: 2,
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 1.5,
        bgcolor: alpha(BLUE, 0.03), borderBottom: '1px solid', borderColor: 'divider',
        borderRadius: '8px 8px 0 0', flexWrap: 'wrap',
      }}>
        <InventoryIcon sx={{ fontSize: 20, color: BLUE }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BLUE, flex: 1 }}>
          DMV Packet
        </Typography>
        <Tooltip title="Recheck and save the packet state">
          <span>
            <IconButton size="small" disabled={loading} onClick={() => load(true)}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
        {readiness && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              {inPacketCount} in packet · {pct}%
            </Typography>
            {(() => {
              const chip = {
                ready: { label: 'Ready to process', color: theme.palette.success.main, text: theme.palette.success.dark },
                waiting_on_person: {
                  label: waitingOn.length ? `Waiting on ${waitingOn.join(' and ')}` : 'Waiting on a rep',
                  color: theme.palette.info.main, text: theme.palette.info.dark,
                },
                needs_review: {
                  label: `${blockers} blocker${blockers === 1 ? '' : 's'}`,
                  color: theme.palette.error.main, text: theme.palette.error.main,
                },
              }[state];
              return (
                <Chip
                  size="small"
                  label={chip.label}
                  sx={{
                    height: 22, fontSize: '0.7rem', fontWeight: 600,
                    bgcolor: alpha(chip.color, 0.12),
                    color: chip.text,
                    border: '1px solid',
                    borderColor: alpha(chip.color, 0.3),
                  }}
                />
              );
            })()}
          </>
        )}
      </Box>

      <Box sx={{ p: 2.5 }}>
        {loading && !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} sx={{ color: BLUE }} /></Box>
        ) : error ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />
            <Typography variant="body2" sx={{ color: 'error.main', flex: 1 }}>{error}</Typography>
            <Button size="small" onClick={() => load(false)} data-sold-ok>Retry</Button>
          </Box>
        ) : (
          <Stack spacing={2}>
            {/* What is true about this deal, as one sentence.
                Where the title goes and whether the consignor signed it over are
                decisions about the DEAL. As two open dropdowns they took eight
                lines here and pushed the document list off the screen. Each
                value is now part of a sentence and opens its own editor, so the
                software's reasoning is there when you question it. */}
            <Box>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Box component="span" sx={{ color: 'text.secondary' }}>Title to</Box>
                <FactValue
                  label="Title goes to"
                  value={trackFact.effective || (data.case_type === 'in_state' ? 'to_customer' : 'to_skyway')}
                  options={[
                    { value: 'to_customer', short: 'the customer', label: 'The customer' },
                    { value: 'to_skyway', short: 'Skyway Classics', label: 'Skyway Classics' },
                  ]}
                  reason={trackAuto.reason}
                  setByName={trackFact.setBy === 'clerk' ? ((trackFact.set || {}).by_name || 'a person') : null}
                  disagrees={trackFact.disagrees}
                  busy={isBusy('fact-track')}
                  onPick={(v) => setFact('track', v)}
                  onAutomatic={() => setFact('track', null, { remove: true })}
                />

                {summary.is_consignment && (
                  <>
                    <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
                    <Box component="span" sx={{ color: 'text.secondary' }}>consignor</Box>
                    <FactValue
                      label="Consignor signed the title over"
                      value={consignorFact.setBy === 'clerk' ? String(consignorFact.effective) : 'auto'}
                      options={[
                        {
                          value: 'auto',
                          short: consignorAuto.value === null
                            ? 'may not have signed'
                            : consignorAuto.value ? 'signed the title' : 'did not sign',
                          label: `Let the software decide${consignorAuto.value === null
                            ? ' (it cannot tell)'
                            : consignorAuto.value ? ' (yes)' : ' (no)'}`,
                        },
                        { value: 'true', short: 'signed the title', label: 'Yes — they signed it' },
                        { value: 'false', short: 'did not sign', label: 'No — a title clerk signs for them' },
                      ]}
                      reason={consignorAuto.reason}
                      setByName={consignorFact.setBy === 'clerk' ? ((consignorFact.set || {}).by_name || 'a person') : null}
                      disagrees={consignorFact.disagrees}
                      busy={isBusy('fact-consignor_signed_title')}
                      onPick={(v) => (v === 'auto'
                        ? setFact('consignor_signed_title', null, { remove: true })
                        : setFact('consignor_signed_title', v === 'true'))}
                      onAutomatic={() => setFact('consignor_signed_title', null, { remove: true })}
                    />
                    <Chip size="small" label="consignment" sx={{ height: 18, fontSize: '0.68rem' }} />

                    {/* A lost or damaged title means the first trip is for a
                        duplicate. The server swaps the packet to the 82101 and
                        the consignor's POA, licence and certificate. Turning it
                        off once the duplicate is back starts the transfer clean. */}
                    <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
                    <FactValue
                      label="Packet for this trip"
                      value={duplicateFact.effective ? 'true' : 'false'}
                      options={[
                        { value: 'false', short: 'transfer packet', label: 'Transfer — the title is in hand' },
                        { value: 'true', short: 'duplicate title packet', label: 'Duplicate title — apply for a new one first' },
                      ]}
                      reason={duplicateAuto.reason}
                      setByName={duplicateFact.setBy === 'clerk' ? ((duplicateFact.set || {}).by_name || 'a person') : null}
                      disagrees={duplicateFact.disagrees}
                      busy={isBusy('fact-needs_duplicate')}
                      onPick={(v) => setFact('needs_duplicate', v === 'true')}
                      onAutomatic={() => setFact('needs_duplicate', null, { remove: true })}
                    />
                  </>
                )}

                {/* The tag on the agency's Dealer Control Sheet. Nothing on the
                    deal records a plate the buyer already owns and wants moved
                    across, so the software can only default to a new tag — this
                    is where the clerk marks the transfers before the stack goes
                    over. It changes the cover sheet only, never the manifest. */}
                <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
                <Box component="span" sx={{ color: 'text.secondary' }}>agency issues</Box>
                <FactValue
                  label="Tag on the control sheet"
                  value={tagFact.setBy === 'clerk' ? String(tagFact.effective) : 'auto'}
                  options={[
                    { value: 'auto', short: TAG_LABELS[tagAuto.value] || 'a new tag', label: `Let the software decide (${TAG_LABELS[tagAuto.value] || 'a new tag'})` },
                    { value: 'new', short: TAG_LABELS.new, label: 'A brand new tag' },
                    { value: 'transfer_only', short: TAG_LABELS.transfer_only, label: 'Transfer only — the buyer moves a plate across' },
                    { value: 'transfer_renew', short: TAG_LABELS.transfer_renew, label: 'Transfer and renew' },
                    { value: 'none', short: TAG_LABELS.none, label: 'No tag — fast title only' },
                  ]}
                  reason={tagAuto.reason}
                  setByName={tagFact.setBy === 'clerk' ? ((tagFact.set || {}).by_name || 'a person') : null}
                  disagrees={tagFact.disagrees}
                  busy={isBusy('fact-tag_type')}
                  onPick={(v) => (v === 'auto'
                    ? setFact('tag_type', null, { remove: true })
                    : setFact('tag_type', v))}
                  onAutomatic={() => setFact('tag_type', null, { remove: true })}
                />
              </Typography>

              {/* Which signed PDF the pages are cut out of. One line each, with
                  the picking and the uploading behind it. */}
              <SourceLine
                label="Signed packet"
                source={source}
                era={data.era}
                uploading={isBusy('replace-source-sales_doc')}
                onOpen={() => setSourceDialog('sales_doc')}
              />
              {summary.is_consignment && (
                <SourceLine
                  label="Consignment agreement"
                  source={sourceConsign}
                  uploading={isBusy('replace-source-consignment_doc')}
                  onOpen={() => setSourceDialog('consignment_doc')}
                />
              )}
            </Box>

            <Box sx={{ height: 6, bgcolor: alpha(BLUE, 0.1), borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: `${pct}%`, borderRadius: 3, transition: 'width 0.4s ease',
                         bgcolor: blockers === 0 ? theme.palette.success.main : BLUE }} />
            </Box>

            {/* Required documents */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Required documents — in the order they print
              </Typography>
              <Stack spacing={0.25} sx={{ mt: 0.75 }}>
                {requirements.map(req => {
                  if (req.in_packet === true) satisfiedSeen += 1;
                  return (
                    <RequirementRow
                      key={req.key}
                      index={satisfiedSeen}
                      req={req}
                      isBusy={isBusy}
                      isUploading={isUploading}
                      onCut={onCut}
                      onMake={onMake}
                      onAttach={onAttach}
                      onUpload={onUpload}
                      onEditData={String(req.doctype || '').startsWith('title') ? () => setTitleDataOpen(true) : null}
                      isAdmin={isAdmin}
                      onWaive={(r, reason) => setWaiver(r, reason, false)}
                      onUnwaive={(r) => setWaiver(r, null, true)}
                      onAsk={(r, force) => askFor(r, force)}
                      onRemove={removeOne}
                      onAllowAgain={allowAgain}
                    />
                  );
                })}
              </Stack>
            </Box>

            {/* The final check before printing: every date the packet prints,
                where it came from, and a way to correct it. A correction
                rebuilds the affected pages straight away. Findings never block;
                the clerk decides. */}
            {data.packet_dates && (
              <Box>
                <PacketDates
                  stockNumber={stockNumber}
                  dates={data.packet_dates}
                  findings={dateFindingList}
                  requirements={requirements}
                  isConsignment={!!summary.is_consignment}
                  onChanged={buildPacket}
                />
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.disabled' }}>
                  Last transfer on the title: {lastTransfer.date || 'unknown'}
                  {lastTransfer.set_by_clerk ? ' (set by hand)' : ''}
                  <Button size="small" onClick={() => setTitleDataOpen(true)} sx={{ ml: 1 }}>Edit title data</Button>
                </Typography>
              </Box>
            )}

            {/* Documents in the packet that no requirement claims any more.
                These still print — the compile step merges the file list without
                consulting the rules — so a leftover from a changed deal reaches
                the state office on a stack that shows nothing wrong. */}
            {orphanedFiles.length > 0 && (
              <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                  {orphanedFiles.length === 1
                    ? 'A document in this packet is not on the list'
                    : `${orphanedFiles.length} documents in this packet are not on the list`}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                  This deal no longer needs {orphanedFiles.length === 1 ? 'it' : 'them'}, but {orphanedFiles.length === 1 ? 'it' : 'they'} will still print. Check before you hand the stack over.
                </Typography>
                <Stack spacing={0.5}>
                  {orphanedFiles.map((f, i) => (
                    <Box key={f.path || i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ flex: 1 }}>{f.name || f.requirement_key}</Typography>
                      <Button size="small" onClick={() => openStoragePath(f.path)} data-sold-ok>View</Button>
                      <Button size="small" color="error"
                        onClick={() => removeOne({ key: f.requirement_key, path: f.path, label: f.name || f.requirement_key })}>
                        Take it out
                      </Button>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Extras */}
            <Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
                  Additional documents{extras.length + heldFiles.length ? ` (${extras.length + heldFiles.length})` : ''}
                </Typography>
                {/* Forms the clerk needs now and then. They go in as extras, so
                    the packet never waits on one. */}
                <FormControl size="small" sx={{ minWidth: 190 }}>
                  <InputLabel>Add a form</InputLabel>
                  <Select
                    label="Add a form"
                    MenuProps={menuProps}
                    value=""
                    disabled={isBusy('extra-form')}
                    onChange={(e) => e.target.value && makeExtraForm(e.target.value)}
                  >
                    <MenuItem value="duplicate_title">Duplicate Title (82101)</MenuItem>
                    <MenuItem value="affidavit">Affidavit</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {/* The duplicate title reads the title photo first, which can
                  take most of a minute. Say so, or it looks frozen. */}
              {isBusy('extra-form') && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    Making the form. Reading the title photo can take up to a minute.
                  </Typography>
                </Box>
              )}
              <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                {extras.map((file, i) => (
                  <ExtraFileRow
                    key={file.path || i}
                    file={file}
                    onView={() => openStoragePath(file.path)}
                    onPrint={() => printStoragePath(file.path, file.name)}
                    onRename={(name) => updateFiles((packet.files || []).map(f => f.path === file.path ? { ...f, name } : f))}
                    onToggleHeld={() => setHeld(file, true)}
                    onDelete={async () => {
                      if (!window.confirm(`Delete "${file.name}" from the packet?`)) return;
                      // Gone from the list immediately; restored if the server disagrees.
                      const kept = packet.files || [];
                      setPacket(prev => ({ ...prev, files: (prev.files || []).filter(f => f.path !== file.path) }));
                      const res = await RequestManager.post({
                        function: 'removeDmvPacketEntry',
                        variables: { stockNumber, path: file.path, removedBy: StateManager.userID || null },
                      });
                      if (!res || res.success === false) {
                        setPacket(prev => ({ ...prev, files: kept }));
                        return StateManager.setAlertAndOpen((res && (res.message || res.error)) || 'Could not remove that file.', 'error');
                      }
                      StateManager.setAlertAndOpen('File removed.', 'success');
                      onPacketChanged?.();
                    }}
                  />
                ))}
                {heldFiles.map((file, i) => (
                  <ExtraFileRow
                    key={file.path || `held-${i}`}
                    file={file}
                    held
                    onView={() => openStoragePath(file.path)}
                    onPrint={() => printStoragePath(file.path, file.name)}
                    onRename={async (name) => {
                      const held_files = heldFiles.map(f => f.path === file.path ? { ...f, name } : f);
                      setPacket(prev => ({ ...prev, held_files }));
                      await firebase.firestore().doc('dmv_packet/' + stockNumber).set({ held_files }, { merge: true });
                    }}
                    onToggleHeld={() => setHeld(file, false)}
                    onDelete={async () => {
                      if (!window.confirm(`Delete "${file.name}"?`)) return;
                      // The server deletes only what is in files, so put it back there
                      // first and let removeDmvPacketEntry decide what to delete.
                      if (!(await setHeld(file, false))) return;
                      const res = await RequestManager.post({
                        function: 'removeDmvPacketEntry',
                        variables: { stockNumber, path: file.path, removedBy: StateManager.userID || null },
                      });
                      if (!res || res.success === false) {
                        await setHeld(file, true);
                        return StateManager.setAlertAndOpen((res && (res.message || res.error)) || 'Could not remove that file.', 'error');
                      }
                      await readPacketDoc();
                      StateManager.setAlertAndOpen('File removed.', 'success');
                      onPacketChanged?.();
                    }}
                  />
                ))}
                <ExtraDropzone onDrop={onDropExtras} uploading={uploadingExtra} />
              </Stack>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                size="small" variant="contained"
                disabled={!canBuild || isBusy('build')}
                startIcon={isBusy('build') ? <CircularProgress size={13} sx={{ color: 'white' }} /> : <BoltIcon sx={{ fontSize: 16 }} />}
                onClick={buildPacket}
                sx={{ fontSize: '0.78rem', bgcolor: BLUE, '&:hover': { bgcolor: alpha(BLUE, 0.85) } }}
              >
                {isBusy('build') ? 'Building…'
                  : staleCount ? `Build packet (update ${staleCount} page${staleCount === 1 ? '' : 's'})`
                  : 'Build packet'}
              </Button>
              {/* Rule out what the state does not want before chasing anyone for
                  it. Reads the paper title, so it is a click, not automatic. */}
              {exemptable.length > 0 && (
                <Tooltip title="Read the title and work out which of these documents this deal does not need">
                  <Button
                    size="small" variant="outlined"
                    disabled={isBusy('exemptions')}
                    startIcon={isBusy('exemptions')
                      ? <CircularProgress size={13} />
                      : <AutoAwesomeIcon sx={{ fontSize: 15 }} />}
                    onClick={() => checkExemptions(exemptable.some(r => r.exemption))}
                    sx={{ fontSize: '0.78rem', borderColor: BLUE, color: BLUE }}
                  >
                    {isBusy('exemptions') ? 'Checking…' : "Check what's not needed"}
                  </Button>
                </Tooltip>
              )}
              {/* Chase every rep who owes something, in one action. */}
              {askable.length > 0 && (
                <Button
                  size="small" variant="outlined"
                  disabled={isBusy('ask-all')}
                  startIcon={isBusy('ask-all')
                    ? <CircularProgress size={13} />
                    : <NotificationsActiveIcon sx={{ fontSize: 15 }} />}
                  onClick={() => askFor(null)}
                  sx={{ fontSize: '0.78rem', borderColor: BLUE, color: BLUE }}
                >
                  {isBusy('ask-all') ? 'Asking…' : `Ask reps for ${askable.length}`}
                </Button>
              )}

              <Box sx={{ flex: 1 }} />

              {packet.compiled_path && (
                <Button size="small" variant="outlined" startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                  onClick={() => openStoragePath(packet.compiled_path)}
                  data-sold-ok
                  sx={{ fontSize: '0.78rem', borderColor: BLUE, color: BLUE }}>
                  Open PDF
                </Button>
              )}
              <Button
                size="small" variant="contained"
                disabled={compiling || !(packet.files || []).length}
                startIcon={compiling ? <CircularProgress size={13} sx={{ color: 'white' }} /> : <PrintIcon sx={{ fontSize: 16 }} />}
                onClick={compile}
                sx={{ fontSize: '0.78rem', bgcolor: GOLD, '&:hover': { bgcolor: alpha(GOLD, 0.85) } }}
              >
                {compiling ? 'Compiling…' : packet.compiled_at ? 'Recompile & Print' : 'Compile & Print'}
              </Button>
            </Box>

            {packet.compiled_at && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                Last compiled {new Date(packet.compiled_at).toLocaleString()}
              </Typography>
            )}

            {state !== 'ready' && (packet.review_reasons || []).length > 0 && (() => {
              const waiting = state === 'waiting_on_person';
              const tone = waiting ? theme.palette.info.main : theme.palette.warning.main;
              return (
                <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: alpha(tone, 0.07),
                           border: '1px solid', borderColor: alpha(tone, 0.3) }}>
                  <Typography variant="caption"
                    sx={{ fontWeight: 700, color: waiting ? 'info.dark' : 'warning.dark', display: 'block' }}>
                    {waiting
                      ? `Waiting on ${waitingOn.join(' and ') || 'a rep'} — already asked`
                      : 'Needs review'}
                  </Typography>
                  {(packet.review_reasons || []).map((r, i) => (
                    <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>• {r}</Typography>
                  ))}
                  {waiting && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 0.5 }}>
                      Nothing for you to do — the packet finishes itself when the document arrives.
                    </Typography>
                  )}
                  {packet.compile_error && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'error.main', mt: 0.5 }}>
                      The packet PDF could not be built: {packet.compile_error}
                    </Typography>
                  )}
                </Box>
              );
            })()}
          </Stack>
        )}
      </Box>

      <TitleAppDialog
        open={titleDataOpen}
        onClose={() => setTitleDataOpen(false)}
        car={car}
        caseType={data ? data.case_type : null}
        onGenerated={() => after('Title application added to the packet.')}
      />

      {sourceDialog && (
        <SourceDialog
          open
          onClose={() => setSourceDialog(null)}
          title={sourceDialog === 'consignment_doc'
            ? 'Signed consignment agreement'
            : 'Signed sales paperwork'}
          source={sourceDialog === 'consignment_doc' ? sourceConsign : source}
          sourceField={sourceDialog}
          onChoose={chooseSource}
          onReplace={(field, file) => { setSourceDialog(null); replaceSource(field, file); }}
          busy={isBusy('choose-source')}
        />
      )}

      <AffidavitDialog
        open={affidavitOpen}
        onClose={() => setAffidavitOpen(false)}
        car={car}
        onCreated={() => after('Affidavit added to the packet.')}
      />
    </Paper>
  );
}
